/******************************************************************************
 *** CashBookMng ** 운영비 / 특별회계기금 공용 * 현금출납부
*******************************************************************************/
import React from "react";
import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";
import Util from "@/js/common/Util";
import SelectBox from "@/js/common/ui/input/SelectBox";
import { fldInfsProps } from "@/js/common/ui/GridTable";
import Button from "@/js/common/ui/Button";
import { exportToXlsx } from "@/js/common/ExcelExport";
import * as Atom from "@/js/common/Atom";
import { useRecoilValue } from "recoil";

type CashBookRow = {
    trnsDt: string;
    seqNo: any;
    bgtSbjct1stNm: any;
    bgtSbjct2stNm: any;
    bgtSbjct3stNm: any;
    abstrCn: any;
    incomeAmt: number | string;
    expenseAmt: number | string;
    balanceAmt: number | string;
    docNo: any;
    excelRowStyle?: {
        fillColor?: string;
        fontColor?: string;
        bold?: boolean;
    };
};

type CashBookMngProps = {
    systemCode?: string;   // 운영비: "01", 특별회계: "C1" ~ "C5" 또는 별도 코드
};

const CashBookMng = ({
    systemCode,
}: CashBookMngProps) => {
    const userInfo = useRecoilValue(Atom.userInfo);

    // 검색 조건
    const [upOgnzCd   , setUpOgnzCd   ] = React.useState<string>(userInfo.upOgnzCd);       // 상위조직코드(본부)
    const [ognzCd     , setOgnzCd     ] = React.useState<string>(userInfo.ognzCd);         // 조직코드(지부)
    const [acntgYear  , setAcntgYear  ] = React.useState<string>(Util.getNowAcntgYear());  // 회계연도
    const [acntgMonth , setAcntgMonth ] = React.useState<string>(String(new Date().getMonth() + 1).padStart(2, "0")); // 월별 조회

    const [cashBookDataArr, setCashBookDataArr] = React.useState<any[]>([]);

    const cashBookFldInfs: fldInfsProps[] = [
        { type: "text" , fldNm: "trnsDt"        , width: 100, headAlign: "center", dataAlign: "center", label: "날짜"     },
        { type: "text" , fldNm: "seqNo"         , width: 60 , headAlign: "center", dataAlign: "center", label: "순번"     },
        { type: "text" , fldNm: "bgtSbjct1stNm" , width: 100, headAlign: "center", dataAlign: "center", label: "관"       },
        { type: "text" , fldNm: "bgtSbjct2stNm" , width: 100, headAlign: "center", dataAlign: "center", label: "항"       },
        { type: "text" , fldNm: "bgtSbjct3stNm" , width: 130, headAlign: "center", dataAlign: "center", label: "목"       },
        { type: "text" , fldNm: "abstrCn"       , width: 220, headAlign: "center", dataAlign: "left"  , label: "적요"     },
        { type: "comma", fldNm: "incomeAmt"     , width: 110, headAlign: "center", dataAlign: "right" , label: "수입"     },
        { type: "comma", fldNm: "expenseAmt"    , width: 110, headAlign: "center", dataAlign: "right" , label: "지출"     },
        { type: "comma", fldNm: "balanceAmt"    , width: 110, headAlign: "center", dataAlign: "right" , label: "잔액"     },
        { type: "text" , fldNm: "docNo"         , width: 140, headAlign: "center", dataAlign: "center", label: "문서번호" },
    ];

    /** 시스템별 제목 */
    function getPageTitle() {
        if (!systemCode) return "현금출납부";

        const map: any = {
            C1: "복지기금 현금출납부",
            C2: "자립기금 현금출납부",
            C3: "발전기금 현금출납부",
            C4: "희생자부조금 현금출납부",
            C5: "퇴직급여충당금 현금출납부",
            E1: "지부운영비 현금출납부",
            F1: "지부장직무활동비 현금출납부",
            G1: "지부대회비 현금출납부",
        };

        return map[systemCode] || "특별회계기금 현금출납부";
    }

    const pageTitle = getPageTitle();

    const monthNum = Number(acntgMonth);
    const displayYear = (monthNum === 1 || monthNum === 2) ? String(Number(acntgYear) + 1) : acntgYear;
    const displayMonth = acntgMonth ? String(monthNum) : "전체";

    const showCarryForward =
        !!acntgMonth && acntgMonth !== "03" &&
        Number((acntgMonth === "01" || acntgMonth === "02" ? Number(acntgYear) + 1 : acntgYear) + acntgMonth)
            <= Number(new Date().getFullYear().toString() + String(new Date().getMonth() + 1).padStart(2, "0"));

    React.useEffect(() => {
        AppContext.showSpinner();

        Axios.dataAccess({
            url: "system/bgt/mbsds/cmptn/select-cash-book-list.do",
            methodType: "post",
            paramType: "json",
            paramData: {
                systemCode       : systemCode,
                upOrgCd          : upOgnzCd,
                orgCd            : ognzCd,
                acntgYear        : acntgYear,
                acntgMonth       : acntgMonth,
                showCarryForward : showCarryForward,
            },
            onSuccessFn: (res: any) => {
                setCashBookDataArr(res.result.list || []);
                AppContext.hideSpinner();
            },
        });
    }, [systemCode, upOgnzCd, ognzCd, acntgYear, acntgMonth, showCarryForward]);

    function makeCashBookRows(list: any[]) {
        let balance = 0;
        let totalIncomeAmt = 0;
        let totalExpenseAmt = 0;

        const rows: CashBookRow[] = list.map((item: any) => {
            const incomeAmt = Number(item.incomeAmt || 0);
            const expenseAmt = Number(item.expenseAmt || 0);

            balance += incomeAmt - expenseAmt;
            totalIncomeAmt += incomeAmt;
            totalExpenseAmt += expenseAmt;

            return {
                trnsDt        : Util.cvtDateStrForm(String(item.trnsDt), "-", "YYYYMMDD"),
                seqNo         : item.seqNo,
                bgtSbjct1stNm : item.bgtSbjct1stNm,
                bgtSbjct2stNm : item.bgtSbjct2stNm,
                bgtSbjct3stNm : item.bgtSbjct3stNm,
                abstrCn       : item.abstrCn,
                incomeAmt     : incomeAmt,
                expenseAmt    : expenseAmt,
                balanceAmt    : balance,
                docNo         : item.docNo,
            };
        });

        if (rows.length === 0) {
            return [];
        }

        rows.push({
            trnsDt        : "합계",
            seqNo         : "",
            bgtSbjct1stNm : "",
            bgtSbjct2stNm : "",
            bgtSbjct3stNm : "",
            abstrCn       : "",
            incomeAmt     : totalIncomeAmt,
            expenseAmt    : totalExpenseAmt,
            balanceAmt    : balance,
            docNo         : "",
            excelRowStyle : {
                fillColor: "FFEF99",
                fontColor: "000000",
                bold: true,
            },
        });

        return rows;
    }

    function downloadCashBookExcel() {
        if (cashBookDataArr.length === 0) {
            AppContext.openAlert({
                title: "데이터 확인",
                msg: "엑셀로 다운로드할 데이터가 없습니다.",
                icon: "warning",
            });
            return;
        }

        exportToXlsx({
            fileName: `${displayYear}년 ${acntgMonth ? `${displayMonth}월 ` : ""}${pageTitle}`,
            title: `${displayYear}년 ${acntgMonth ? `${displayMonth}월 ` : ""}${pageTitle}`,
            subTitleLeft: `${acntgYear}회계년도`,
            subTitleRight: "(금액단위:원)",
            columns: cashBookFldInfs,
            rows: makeCashBookRows(cashBookDataArr),
        });
    }

    return (
        <div className="w1200 bgcWhite p20 bdrd8 mt10 boxShadowSoft">
            {/* 타이틀 */}
            <div className="mt5">
                <span className="dpBlock w100p fm-c fs25 fc2c3e50 ls1 pb10">
                    {pageTitle}
                </span>
                <div className="bdt3 bdc007bff pb10" />
            </div>

            {/* 검색 영역 */}
            <div className="bd1 bdcDDDDDD p20 mb10 bdrd6 bgcFAFAFA">
                <div className="dpFlx aiC flxWrap gap14">
                    <div className="dpFlx aiC">
                        <span className="w50 fm-c fs15 fc333333 ls1 taL">본부</span>
                        <SelectBox
                            value={upOgnzCd}
                            width={150}
                            height={36}
                            fontSize={14}
                            fontFamily="fm-c"
                            color="#333"
                            letterSpacing={-1}
                            borderColor="#ddd"
                            borderOnColor="#222"
                            borderRadius={2}
                            background="#F6F6F6"
                            codeArray={Util.getUpOrgCodeArray("01")}
                            placeholder="전체"
                            paddingRight={8}
                            onChangeFunc={(data: string) => setUpOgnzCd(data)}
                            disabled={true}
                        />
                    </div>

                    <div className="dpFlx aiC">
                        <span className="w50 fm-c fs15 fc333333 ls1 taL">지부</span>
                        <SelectBox
                            value={ognzCd}
                            width={150}
                            height={36}
                            fontSize={14}
                            fontFamily="fm-c"
                            color="#333"
                            letterSpacing={-1}
                            borderColor="#ddd"
                            borderOnColor="#222"
                            borderRadius={2}
                            background="#F6F6F6"
                            codeArray={Util.getDownOrgCodeArray(upOgnzCd)}
                            placeholder="전체"
                            paddingRight={8}
                            onChangeFunc={(data: string) => setOgnzCd(data)}
                            disabled={true}
                        />
                    </div>

                    <div className="dpFlx aiC">
                        <span className="w80 fm-c fs15 fc333333 ls1 taL">회계연도</span>
                        <SelectBox
                            value={acntgYear}
                            width={120}
                            height={36}
                            fontSize={14}
                            fontFamily="fm-c"
                            color="#333"
                            letterSpacing={-1}
                            borderColor="#ddd"
                            borderOnColor="#222"
                            borderRadius={2}
                            background="#fff"
                            codeArray={Util.getAcntgYearArray()}
                            placeholder="선택"
                            paddingRight={8}
                            onChangeFunc={(data: string) => setAcntgYear(data)}
                        />
                    </div>

                    <div className="dpFlx aiC">
                        <span className="w80 fm-c fs15 fc333333 ls1 taL">월별 조회</span>
                        <SelectBox
                            value={acntgMonth}
                            width={140}
                            height={36}
                            fontSize={14}
                            fontFamily="fm-c"
                            color="#333"
                            letterSpacing={-1}
                            borderColor="#ddd"
                            borderOnColor="#222"
                            borderRadius={2}
                            background="#fff"
                            paddingRight={8}
                            codeArray={[{ code: "", name: "전체" }].concat(Util.getCodeArray("SRCH_MM"))}
                            placeholder="전체"
                            onChangeFunc={(data: string) => setAcntgMonth(data)}
                        />
                    </div>

                    <div className="mlA">
                        <Button
                            buttonName="현금출납부 Excel"
                            width={150}
                            height={36}
                            fontFamily="fm-b"
                            fontSize={14}
                            letterSpacing={-1}
                            variant="styExcelBtn"
                            borderRadius={5}
                            buttonClass="dpFlx aiC jcC"
                            onClickFunc={() => downloadCashBookExcel()}
                        />
                    </div>
                </div>
            </div>

            {/* 리스트 */}
            <div className="w100p bsBdb bgcFDFCFC">
                <div
                    className="w100p"
                    style={{ borderTop: "1px solid #C5C5C5", position: "relative", zIndex: 0 }}
                >
                    <table className="w100p tlF tbl-collapse" style={{ tableLayout: "fixed" }}>
                        <colgroup>
                            <col style={{ width: 110 }} />
                            <col style={{ width: 50 }} />
                            <col style={{ width: 70 }} />
                            <col style={{ width: 100 }} />
                            <col style={{ width: 130 }} />
                            <col style={{ width: 200 }} />
                            <col style={{ width: 130 }} />
                            <col style={{ width: 130 }} />
                            <col style={{ width: 130 }} />
                            <col style={{ width: 160 }} />
                        </colgroup>

                        <thead>
                            <tr>
                                {["날짜", "순번", "관", "항", "목", "적요", "수입", "지출", "잔액", "문서번호"].map((label) => (
                                    <th key={label} className="p0 bd1 bdcEDEDED">
                                        <div className="h40 dpFlx aiC jcC pw12 bgcF1F8FF">
                                            <div className="w100p fm-c fs13 fc34495e wsNw ofH toE taC">
                                                {label}
                                            </div>
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        <tbody>
                            {makeCashBookRows(cashBookDataArr).length === 0 ? (
                                <tr>
                                    <td colSpan={10} className="bd1 bdcEDEDED p16 taC fm-Pretendard-Regular fs13 fc999999">
                                        조회된 데이터가 없습니다.
                                    </td>
                                </tr>
                            ) : (
                                makeCashBookRows(cashBookDataArr).map((row: CashBookRow, idx: number) => {
                                    const isTotalRow = row.trnsDt === "합계";

                                    return (
                                        <tr
                                            key={idx}
                                            className={isTotalRow ? "" : "bgcF9F9F9-hover transitionBgc"}
                                        >
                                            {/* 날짜 */}
                                            <td className="bd1 bdcEDEDED p0">
                                                <div className={`h35 dpFlx aiC jcC pw12 ${
                                                    isTotalRow ? "bgcF3E7A1" : ""
                                                }`}>
                                                    <div className={`w100p wsNw ofH toE taC ${
                                                        isTotalRow
                                                            ? "fm-c fs12 fc111111"
                                                            : "fm-c fs12 fc666666"
                                                    }`}>
                                                        {row.trnsDt}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* 순번 */}
                                            <td className="bd1 bdcEDEDED p0">
                                                <div className={`h35 dpFlx aiC jcC pw12 ${
                                                    isTotalRow ? "bgcF3E7A1" : ""
                                                }`}>
                                                    <div className={`w100p wsNw ofH toE taC ${
                                                        isTotalRow
                                                            ? "fm-c fs12 fc111111"
                                                            : "fm-c fs12 fc666666"
                                                    }`}>
                                                        {row.seqNo}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* 관 */}
                                            <td className="bd1 bdcEDEDED p0">
                                                <div className={`h35 dpFlx aiC pw12 ${
                                                    isTotalRow ? "bgcF3E7A1" : ""
                                                }`}>
                                                    <div className={`w100p wsNw ofH toE ${
                                                        isTotalRow
                                                            ? "fm-c fs12 fc111111"
                                                            : "fm-c fs12 fc666666"
                                                    }`}>
                                                        {row.bgtSbjct1stNm}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* 항 */}
                                            <td className="bd1 bdcEDEDED p0">
                                                <div className={`h35 dpFlx aiC pw12 ${
                                                    isTotalRow ? "bgcF3E7A1" : ""
                                                }`}>
                                                    <div className={`w100p wsNw ofH toE ${
                                                        isTotalRow
                                                            ? "fm-c fs12 fc111111"
                                                            : "fm-c fs12 fc666666"
                                                    }`}>
                                                        {row.bgtSbjct2stNm}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* 목 */}
                                            <td className="bd1 bdcEDEDED p0">
                                                <div className={`h35 dpFlx aiC pw12 ${
                                                    isTotalRow ? "bgcF3E7A1" : ""
                                                }`}>
                                                    <div className={`w100p wsNw ofH toE ${
                                                        isTotalRow
                                                            ? "fm-c fs12 fc111111"
                                                            : "fm-c fs12 fc666666"
                                                    }`}>
                                                        {row.bgtSbjct3stNm}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* 적요 */}
                                            <td className="bd1 bdcEDEDED p0">
                                                <div className={`h35 dpFlx aiC pw12 ${
                                                    isTotalRow ? "bgcF3E7A1" : ""
                                                }`}>
                                                    <div className={`w100p wsNw ofH toE ${
                                                        isTotalRow
                                                            ? "fm-c fs12 fc111111"
                                                            : "fm-c fs12 fc666666"
                                                    }`}>
                                                        {row.abstrCn}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* 수입 */}
                                            <td className="bd1 bdcEDEDED p0">
                                                <div className={`h35 dpFlx aiC jcE pw12 ${
                                                    isTotalRow ? "bgcF3E7A1" : ""
                                                }`}>
                                                    <div className={`w100p wsNw ofH toE taR ${
                                                        isTotalRow
                                                            ? "fm-c fs12 fc111111"
                                                            : "fm-c fs12 fc666666"
                                                    }`}>
                                                        {Util.comma(String(row.incomeAmt))}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* 지출 */}
                                            <td className="bd1 bdcEDEDED p0">
                                                <div className={`h35 dpFlx aiC jcE pw12 ${
                                                    isTotalRow ? "bgcF3E7A1" : ""
                                                }`}>
                                                    <div className={`w100p wsNw ofH toE taR ${
                                                        isTotalRow
                                                            ? "fm-c fs12 fc111111"
                                                            : "fm-c fs12 fc666666"
                                                    }`}>
                                                        {Util.comma(String(row.expenseAmt))}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* 잔액 */}
                                            <td className="bd1 bdcEDEDED p0">
                                                <div className={`h35 dpFlx aiC jcE pw12 ${
                                                    isTotalRow ? "bgcF3E7A1" : ""
                                                }`}>
                                                    <div className={`w100p wsNw ofH toE taR ${
                                                        isTotalRow
                                                            ? "fm-c fs12 fc111111"
                                                            : "fm-c fs12 fc666666"
                                                    }`}>
                                                        {Util.comma(String(row.balanceAmt))}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* 문서번호 */}
                                            <td className="bd1 bdcEDEDED p0">
                                                <div className={`h35 dpFlx aiC jcC pw12 ${
                                                    isTotalRow ? "bgcF3E7A1" : ""
                                                }`}>
                                                    <div className={`w100p wsNw ofH toE taC ${
                                                        isTotalRow
                                                            ? "fm-c fs12 fc111111"
                                                            : "fm-c fs12 fc666666"
                                                    }`}>
                                                        {row.docNo}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default CashBookMng;